import { useState, useEffect, useRef } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { api } from '../../api/client';
import { updateTask } from '../../hooks/useTasks';
import { queryClient } from '../../api/queryClient';
import { useAppStore } from '../../store';

interface ProgressNotePopoverProps {
  taskId: string;
  taskTitle: string;
  progressBefore: number;
  progressAfter: number;
  onClose: () => void;
}

export function ProgressNotePopover({
  taskId,
  taskTitle,
  progressBefore,
  progressAfter,
  onClose,
}: ProgressNotePopoverProps) {
  const [note, setNote] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const spaceId = useAppStore((s) => s.currentSpaceId);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (note.trim()) {
      const updates = await api.progressUpdates.getByTaskId(taskId);
      const recent = updates
        .filter((u: any) => u.progressBefore === progressBefore && u.progressAfter === progressAfter)
        .pop();
      if (recent) {
        await api.progressUpdates.update(recent.id, { note: note.trim() });
        queryClient.invalidateQueries({ queryKey: ['progress-updates', taskId] });
      }
    }
    onClose();
  };

  const handleCancel = async () => {
    const wasCompleted = progressAfter === 100;

    // Optimistically revert the cache so the UI feels instant
    await queryClient.cancelQueries({ queryKey: ['tasks', spaceId, 'track'] });
    queryClient.setQueryData<any[]>(['tasks', spaceId, 'track'], (old) =>
      old?.map((t) =>
        t.id === taskId
          ? { ...t, trackProgress: progressBefore, ...(wasCompleted ? { status: 'track' as const, completedAt: null } : {}) }
          : t
      )
    );
    onClose();

    // Persist the revert to the server in the background
    try {
      await updateTask(taskId, {
        trackProgress: progressBefore,
        ...(wasCompleted ? { status: 'track', completedAt: null } : {}),
      });
    } finally {
      queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
    }

    // Clean up the auto-created progress note (non-critical)
    try {
      const updates = await api.progressUpdates.getByTaskId(taskId);
      const recent = updates
        .filter((u: any) => u.progressBefore === progressBefore && u.progressAfter === progressAfter)
        .pop();
      if (recent) await api.progressUpdates.delete(recent.id);
      queryClient.invalidateQueries({ queryKey: ['progress-updates', taskId] });
    } catch {
      // ignore — note cleanup failing doesn't affect the progress revert
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-base/80 backdrop-blur-sm">
      <div className="surface-overlay w-96 p-0">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
          <div className="flex items-center gap-2 text-text-secondary">
            <MessageSquare size={14} />
            <span className="text-caption font-semibold">Quick update</span>
          </div>
          <button onClick={handleCancel} className="text-text-muted hover:text-text-secondary p-1.5 rounded-lg interactive-ghost">
            <X size={16} />
          </button>
        </div>
        <div className="p-5">
          <p className="text-caption text-text-muted mb-4">
            <span className="text-text-primary font-medium">{taskTitle}</span>
            {' '}moved from {progressBefore}% to {progressAfter}%
          </p>
          <form onSubmit={handleSave} className="space-y-4">
            <textarea
              ref={inputRef}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); handleCancel(); } if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSave(e); } }}
              placeholder="What did you do? (optional)"
              rows={2}
              className="w-full bg-bg-raised border border-border-default rounded-xl px-3.5 py-2.5 text-body
                text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent resize-none field-sizing-content max-h-32"
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={handleCancel} className="px-4 py-2 text-caption text-text-muted hover:text-text-secondary rounded-lg">
                Cancel
              </button>
              <button type="submit" className="px-5 py-2 text-caption font-semibold bg-accent text-bg-base rounded-xl hover:bg-accent-hover transition-colors">
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
